import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useUser } from '@/contexts/UserContext'
import { useWhatsNew } from '@/hooks/use-whats-new'

/**
 * What changed since this person last looked.
 *
 * Mounted app-wide rather than placed on a page, because it has to find the user wherever they
 * land. `CallLogDialog` is the precedent for a global dialog that takes no props.
 *
 *
 * WHY THE GATE IS A SEPARATE OUTER COMPONENT
 *
 * `currentUser` starts null and arrives from an effect, and `usePersistentState` reads
 * localStorage in a `useState` initialiser, which runs ONCE. A hook that mounts before the user
 * resolves therefore initialises against an undefined key and never re-reads: the marker would
 * read as absent for everybody, the baseline would be set on every login, and the dialog would
 * never appear for anyone. An early `return null` inside the hook-calling component does not fix
 * that, because hooks run before the return.
 *
 * So the outer component calls nothing but `useUser` and does not render the inner one until
 * there is a user to read state for.
 */

interface WhatsNewDialogProps {
  /**
   * Hold off while another startup dialog is showing (StartupDialogs). The stored marker is not
   * touched while suppressed, so it opens as soon as the other one closes.
   */
  suppressed?: boolean
}

export function WhatsNewDialog({ suppressed = false }: WhatsNewDialogProps) {
  const { currentUser, isLoading } = useUser()
  if (isLoading || !currentUser?.id) return null
  // Keyed by user so switching accounts remounts against the right stored marker rather than
  // carrying the previous person's state.
  return <WhatsNewDialogForUser key={currentUser.id} suppressed={suppressed} />
}

function WhatsNewDialogForUser({ suppressed }: { suppressed: boolean }) {
  const { unseen, unseenCount, shouldShow, markAllSeen } = useWhatsNew()

  /*
   * Closing is the only way out, and it always marks everything seen.
   *
   * Local `dismissed` exists so the dialog shuts immediately. `markAllSeen` changes the stored
   * marker, which makes `shouldShow` false on the next render anyway, but relying on that alone
   * would leave the close animation fighting a re-render.
   */
  const [dismissed, setDismissed] = useState(false)

  const close = () => {
    setDismissed(true)
    markAllSeen()
  }

  const open = shouldShow && !dismissed && !suppressed
  const hidden = unseenCount - unseen.length

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
            {/* The count is in the title so the decision to read or dismiss is an informed one. */}
            {unseenCount === 1
              ? 'One update since you were last here'
              : `${unseenCount} updates since you were last here`}
          </DialogTitle>
          <DialogDescription>
            New since you last saw this. It will not show again once you close it.
          </DialogDescription>
        </DialogHeader>

        {/*
          A scrollable region needs to be focusable and named, or a keyboard user can reach
          content they cannot scroll to. Enforced by the WCAG spec's scrollable-region checks.
        */}
        <div
          className="overflow-y-auto space-y-4 pr-1"
          tabIndex={0}
          role="group"
          aria-label="What's new"
        >
          {unseen.map((entry) => (
            <div key={entry.id}>
              <p className="text-sm font-medium mb-1">{entry.title}</p>
              {entry.body}
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2 items-stretch sm:items-center">
          <Link
            to="/help#whats-new"
            onClick={close}
            className="text-sm text-primary underline underline-offset-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {hidden > 0 ? `See all updates, including ${hidden} more` : 'See all updates in Help'}
          </Link>
          {/* One button. "Remind me later" would be a second state nobody asked for, and the
              honest version of it is the full list in Help, which is linked. */}
          <Button onClick={close}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
